import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './utils';

const alertVariants = cva(
  'relative flex w-full items-start gap-3 rounded-lg border p-4 text-sm',
  {
    variants: {
      variant: {
        info: 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-100',
        success:
          'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100',
        warning:
          'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100',
        danger:
          'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-100',
      },
    },
    defaultVariants: { variant: 'info' },
  },
);

const iconTint: Record<NonNullable<AlertProps['variant']>, string> = {
  info: 'text-sky-500 dark:text-sky-400',
  success: 'text-emerald-500 dark:text-emerald-400',
  warning: 'text-amber-500 dark:text-amber-400',
  danger: 'text-rose-500 dark:text-rose-400',
};

const iconMap = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
} as const;

export interface AlertProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertVariants> {
  title?: ReactNode;
  description?: ReactNode;
  /** Override the default variant icon. Pass `null` to hide it entirely. */
  icon?: ReactNode | null;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = 'info', title, description, icon, children, ...props }, ref) => {
    const v = variant ?? 'info';
    const IconComp = iconMap[v];
    const renderedIcon =
      icon === null
        ? null
        : icon ?? <IconComp className={cn('mt-0.5 h-5 w-5 shrink-0', iconTint[v])} aria-hidden />;

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ variant: v }), className)}
        {...props}
      >
        {renderedIcon}
        <div className="min-w-0 flex-1 space-y-1">
          {title ? (
            <AlertTitle>{title}</AlertTitle>
          ) : null}
          {description ? (
            <AlertDescription>{description}</AlertDescription>
          ) : null}
          {children}
        </div>
      </div>
    );
  },
);
Alert.displayName = 'Alert';

export const AlertTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn('text-sm font-semibold leading-tight', className)}
      {...props}
    />
  ),
);
AlertTitle.displayName = 'AlertTitle';

export const AlertDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm opacity-90 [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';
